/**
 * Emma's cowboy hat, drawn as a real cattleman: a brim that curls up at both
 * sides and dips at the front, a crown narrower than the brim with a centre
 * crease and two front pinches, and a band.
 *
 * Placement follows Brian's rig exactly (vendor/emma/hat.js, left unmodified):
 * the hat sits on the head's highest point, pitch fattens or thins the brim
 * and foreshortens the crown, yaw slides the crown around the brim, roll tilts
 * the lot. Only the drawing is new, because a flat oval brim under a straight
 * sided crown read as a pilgrim hat.
 */

type Pt = [number, number]
interface Head { yaw: number; pitch: number; roll: number }

const FELT = '#9a6232'
const FELT_SHADE = '#7c4b24'
const FELT_DARK = '#5a3418'
const BAND = '#3b2413'
const CURL = 31 // how far the brim tips turn up
const BRIM_RY = 14

/** Brim outline for a given depth (ry) and height (by): tips raised, front dipped. */
function brim(ry: number, by: number) {
  const tip = by - CURL
  const far = `M-100,${tip} C-86,${by - ry * 0.7 - 8} -48,${by - ry} 0,${by - ry} C48,${by - ry} 86,${by - ry * 0.7 - 8} 100,${tip}`
  const near = `C88,${by + ry * 0.35} 50,${by + ry + 7} 0,${by + ry + 7} C-50,${by + ry + 7} -88,${by + ry * 0.35} -100,${tip}`
  // The half nearest the camera, drawn over the crown's base.
  const front = `M-100,${tip} C-80,${by - 9} -44,${by} 0,${by} C44,${by} 80,${by - 9} 100,${tip} ${near} Z`
  const lip = `M100,${tip} ${near}`
  return { full: `${far} ${near} Z`, front, lip }
}

export function CowboyHat({ id }: { id: string }) {
  const b = brim(BRIM_RY, 0)
  return (
    <g id={id} className="hat" style={{ display: 'none' }}>
      <path id={`${id}Under`} d={b.full} fill={FELT_DARK} />
      <path id={`${id}Brim`} d={b.full} fill={FELT} />
      <g id={`${id}Crown`}>
        {/* Body: slightly tapered, rounded shoulders, a crease down the top. */}
        <path d="M-44,2 C-46,-18 -44,-42 -36,-54 Q-30,-65 -16,-62 Q-6,-52 0,-52 Q6,-52 16,-62 Q30,-65 36,-54 C44,-42 46,-18 44,2 Z" fill={FELT} />
        {/* Shadow down the far side, for volume. */}
        <path d="M28,-58 C40,-44 43,-20 44,2 L35,2 C35,-20 33,-40 22,-55 Z" fill={FELT_SHADE} />
        {/* The two front pinches. */}
        <path d="M-25,-55 Q-22,-40 -13,-31 Q-17,-45 -17,-57 Z" fill={FELT_SHADE} />
        <path d="M25,-55 Q22,-40 13,-31 Q17,-45 17,-57 Z" fill={FELT_SHADE} />
        {/* The centre crease. */}
        <path d="M-16,-61 Q-6,-52 0,-52 Q6,-52 16,-61" fill="none" stroke={FELT_DARK} strokeWidth={3} strokeLinecap="round" />
        {/* Band with a small concho. */}
        <path d="M-45,-15 C-15,-11 15,-11 45,-15 L45,-4 C15,0 -15,0 -45,-4 Z" fill={BAND} />
        <circle cx="-31" cy="-9" r="3.2" fill="#c9a36a" />
      </g>
      <path id={`${id}Front`} d={b.front} fill={FELT} />
      <path id={`${id}Lip`} d={b.lip} fill="none" stroke={FELT_DARK} strokeWidth={3} strokeLinejoin="round" />
    </g>
  )
}

/** Same signature and behaviour as the rig's placeHat. */
export function placeCowboyHat(
  g: Element, P: Pt[], Ex: Head, on: boolean,
  lift = 6, scale = 1.25, dx = 0, tilt = 0, openW = 1,
) {
  const el = g as SVGGElement
  if (!on) { el.style.display = 'none'; return }
  el.style.display = ''
  let tx = 0, ty = 1e9
  for (const p of P) if (p[1] < ty) { ty = p[1]; tx = p[0] }
  const pr = Ex.pitch * Math.PI / 180
  const sp = Math.sin(pr), sy = Math.sin(Ex.yaw * Math.PI / 180)
  const $ = (s: string) => document.getElementById(el.id + s)

  // Pitch down shows the top of the brim; pitch up shows its underside.
  const ry = Math.max(4, BRIM_RY + (-sp) * 38)
  const by = (-sp) * 6
  const th = 2.4 / scale // brim thickness stays about 2px on screen
  const b = brim(ry, by)
  $('Under')?.setAttribute('d', b.full)
  $('Under')?.setAttribute('transform', `translate(0,${th.toFixed(2)})`)
  $('Brim')?.setAttribute('d', b.full)
  $('Front')?.setAttribute('d', b.front)
  $('Lip')?.setAttribute('d', b.lip)
  $('Lip')?.setAttribute('stroke-width', (1.8 / scale).toFixed(2))
  const up = sp > 0.12
  for (const k of ['Brim', 'Front', 'Lip']) { const n = $(k) as SVGElement | null; if (n) n.style.display = up ? 'none' : '' }

  const cs = Math.max(0.55, 1 - 0.45 * Math.max(0, -sp))
  $('Crown')?.setAttribute('transform',
    `translate(${(sy * 16).toFixed(1)},${by.toFixed(1)}) scale(${((1 - 0.08 * Math.abs(sy)) * openW).toFixed(3)},${cs.toFixed(3)})`)
  el.setAttribute('transform',
    `translate(${(tx + dx + Ex.yaw * 0.5).toFixed(1)},${(ty - lift + (-sp) * 10).toFixed(1)}) rotate(${(Ex.roll + Ex.yaw * 0.10 + tilt).toFixed(1)}) scale(${scale})`)
}
